import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, type DigestDetail as DigestDetailT } from '../lib/api';
import { useAuth } from '../lib/auth';

const SRCDOC_MAX = 1_800_000;
const WEEK_RE = /^\d{4}-\d{2}-\d{2}$/;

export function DigestDetail(): JSX.Element {
  const { week } = useParams<{ week: string }>();
  const auth = useAuth();
  const [detail, setDetail] = useState<DigestDetailT | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.idToken === null || !week || !WEEK_RE.test(week)) return;
    const ctrl = new AbortController();
    setDetail(null);
    setError(null);
    api
      .getDigest(auth.idToken, week, ctrl.signal)
      .then((d) => setDetail(d))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'fetch failed');
      });
    return () => ctrl.abort();
  }, [auth.idToken, week]);

  // Back-compat fallback: inject `<base target="_blank">` into the digest
  // HTML so any `<a href>` without its own target opens in a new tab instead
  // of navigating the iframe. Digests built after the to-html.ts fix already
  // carry per-anchor `target="_blank"`; this `<base>` only covers older
  // digests still in S3 with bare anchors. Inside an iframe, plain `href`
  // clicks try to navigate the iframe itself, which the admin CloudFront CSP
  // blocks via `frame-src 'self' blob:` — clicks died with "Framing
  // 'https://...' violates the following Content Security Policy directive"
  // and the iframe got replaced with chrome-error://.
  //
  // The sandbox already permits popups (`allow-popups` +
  // `allow-popups-to-escape-sandbox`). An explicit anchor `target` wins over
  // `<base>`, so the builder fix and this fallback compose cleanly.
  const iframeHtml = useMemo(() => {
    if (!detail) return null;
    return injectBaseTarget(detail.html);
  }, [detail]);

  // Blob URL fallback for HTML bodies larger than the Safari srcdoc ceiling.
  const blobUrl = useMemo(() => {
    if (!iframeHtml || iframeHtml.length <= SRCDOC_MAX) return null;
    const blob = new Blob([iframeHtml], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }, [iframeHtml]);
  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const downloadUrl = useMemo(() => {
    if (!detail) return null;
    // Download = the raw .html the builder produced; no SPA-specific tweaks.
    const blob = new Blob([detail.html], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }, [detail]);
  useEffect(() => () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
  }, [downloadUrl]);

  if (!week || !WEEK_RE.test(week)) {
    return <div className="text-2xs font-mono text-ink-muted">Invalid week format.</div>;
  }
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-2xs font-mono text-ink-muted">
        <Link to="/digests" className="underline">← Back</Link>
        <div className="mt-4 text-red-400">{error}</div>
      </div>
    );
  }
  if (!detail) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-2xs font-mono text-ink-muted">…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-3">
      <div className="flex items-center justify-between text-2xs font-mono text-ink-muted">
        <Link to="/digests" className="underline">← Back to Digests</Link>
        {downloadUrl && (
          <a href={downloadUrl} download={`digest-${week}.html`} className="underline">
            Download .html
          </a>
        )}
      </div>
      <iframe
        title={`Digest ${week}`}
        srcDoc={blobUrl || !iframeHtml ? undefined : iframeHtml}
        src={blobUrl ?? undefined}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        className="w-full min-h-[calc(100vh-12rem)] bg-white rounded"
      />
    </div>
  );
}

// Inject `<base target="_blank">` into <head> so every link opens in a new
// tab. Falls back to prepending the base tag at the document start if no
// <head> tag is found (matches the digest builder's wrapDocument shape).
function injectBaseTarget(html: string): string {
  const baseTag = '<base target="_blank">';
  const headOpen = /<head[^>]*>/i;
  if (headOpen.test(html)) {
    return html.replace(headOpen, (match) => `${match}${baseTag}`);
  }
  return `${baseTag}${html}`;
}
